/**
 * Les bons d'intervention saisis au bureau : libellés, filtres de la liste, et ce que l'on peut faire d'un
 * bon selon son état et les droits de l'utilisateur (lot I1.1, ADR-0014).
 *
 * Aucune règle métier ici : le serveur est l'autorité (il refuse une transition interdite, un bon incomplet,
 * une révision périmée). L'écran n'en reflète que ce qu'il faut pour ne pas proposer un geste voué au refus.
 *
 * Fonctions pures, sans dépendance au navigateur.
 */
import type { BonResume, EtatBon, EtatSuiteBon, GenreLigneBon, GenreSuiteBon } from '../api/client';
import type { DroitsBons } from './droits';

/** Les états, dits en français. Un bon saisi au bureau n'en parcourt que quatre ; les autres sont ceux du planning à venir. */
export const LIBELLE_ETAT: Readonly<Record<EtatBon, string>> = {
  DRAFT: 'Brouillon',
  TO_ASSIGN: 'À affecter',
  PLANNED: 'Planifié',
  DISPATCHED: 'Envoyé',
  IN_PROGRESS: 'En cours',
  COMPLETED_BY_TECH: 'Terminé par le technicien',
  TO_REVIEW: 'À valider',
  REVIEWED: 'Validé',
  TRANSMITTED: 'Transmis',
  CLASSIFIED: 'Classé',
  TO_QUOTE: 'À chiffrer',
  TO_INVOICE: 'À facturer',
  CLOSED: 'Clôturé',
  CANCELLED: 'Annulé',
};

/**
 * La classe de pastille d'un état (`.etat` existe déjà) : un brouillon est gris, un bon à valider orange (il attend
 * quelqu'un), un bon validé vert, un bon annulé rouge.
 */
export function classeEtat(etat: EtatBon): string {
  if (etat === 'CANCELLED') return 'revoque';
  if (etat === 'DRAFT') return 'suspendu';
  if (etat === 'TO_REVIEW') return 'attente';
  return 'actif';
}

export const LIBELLE_GENRE_LIGNE: Readonly<Record<GenreLigneBon, string>> = {
  PART: 'Article',
  LABOR: 'Main d’œuvre',
  TRAVEL: 'Déplacement',
  FLAT_FEE: 'Forfait',
  EQUIPMENT: 'Matériel',
  ACCESSORY: 'Accessoire',
};

/** L'unité proposée d'emblée à un type de ligne (modifiable) : une main-d'œuvre se compte en heures. */
export const UNITE_PAR_DEFAUT: Readonly<Record<GenreLigneBon, string>> = {
  PART: '',
  LABOR: 'h',
  TRAVEL: '',
  FLAT_FEE: '',
  EQUIPMENT: '',
  ACCESSORY: '',
};

/**
 * La désignation proposée d'emblée à une ligne qui n'est pas une pièce (modifiable) : la soumission exige une
 * désignation sur CHAQUE ligne, et « Main d'œuvre » ou « Déplacement » se disent d'eux-mêmes.
 */
export const DESIGNATION_PAR_DEFAUT: Readonly<Record<GenreLigneBon, string>> = {
  PART: '',
  LABOR: 'Main d’œuvre',
  TRAVEL: 'Déplacement',
  FLAT_FEE: 'Forfait',
  EQUIPMENT: '',
  ACCESSORY: '',
};

export const LIBELLE_SUITE: Readonly<Record<GenreSuiteBon, string>> = {
  QUOTE_TO_DO: 'Devis à faire',
  REVISION_TO_PLAN: 'Révision à prévoir',
  PART_TO_ORDER: 'Pièce à commander',
  RETURN_TO_PLAN: 'Retour à prévoir (travail non terminé)',
  WATCH_MACHINE: 'Machine à surveiller',
  REGULATORY_CHECK: 'Contrôle réglementaire',
};

/** L'état d'une suite à donner, dit en français. « Qualifiée » et « convertie » sont réservés aux modules à venir (devis, planning, achats). */
export const LIBELLE_ETAT_SUITE: Readonly<Record<EtatSuiteBon, string>> = {
  OPEN: 'Ouverte',
  DISMISSED: 'Écartée',
  CLOSED: 'Soldée',
  QUALIFIED: 'Qualifiée',
  CONVERTED: 'Convertie',
};

export const GENRES_SUITE: readonly GenreSuiteBon[] = [
  'QUOTE_TO_DO',
  'REVISION_TO_PLAN',
  'PART_TO_ORDER',
  'RETURN_TO_PLAN',
  'WATCH_MACHINE',
  'REGULATORY_CHECK',
];

export const GENRES_LIGNE: readonly GenreLigneBon[] = ['PART', 'LABOR', 'TRAVEL', 'FLAT_FEE', 'EQUIPMENT', 'ACCESSORY'];

export interface ChipStatut {
  readonly id: string;
  readonly libelle: string;
  /** Statuts demandés au serveur (« DRAFT,TO_REVIEW ») ; absent = tous. */
  readonly statuts?: string;
}

/** Les filtres rapides de la liste : « À valider » d'abord, c'est ce que l'on cherche le plus. */
export const CHIPS_STATUT: readonly ChipStatut[] = [
  { id: 'tous', libelle: 'Tous' },
  { id: 'a-faire', libelle: 'À faire', statuts: 'TO_ASSIGN,PLANNED,IN_PROGRESS' },
  { id: 'a-valider', libelle: 'À valider', statuts: 'TO_REVIEW' },
  { id: 'brouillons', libelle: 'Brouillons', statuts: 'DRAFT' },
  { id: 'valides', libelle: 'Validés', statuts: 'REVIEWED' },
  { id: 'annules', libelle: 'Annulés', statuts: 'CANCELLED' },
];

export function chipParId(id: string | null): ChipStatut {
  return CHIPS_STATUT.find((c) => c.id === id) ?? CHIPS_STATUT[0]!;
}

/** « 2026-01-28 » → « 28/01/2026 » ; « — » quand le jour n'est pas encore saisi. */
export function jourFr(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [a, m, j] = iso.slice(0, 10).split('-');
  return a && m && j ? `${j}/${m}/${a}` : iso;
}

/** Aujourd'hui, AAAA-MM-JJ, dans le fuseau de l'ordinateur (le serveur contrôle avec celui de l'espace). */
export function aujourdhuiLocal(maintenant: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${maintenant.getFullYear()}-${p(maintenant.getMonth() + 1)}-${p(maintenant.getDate())}`;
}

/** La machine d'un bon, comme on la dit : sa désignation, sinon « modèle n° série » ; « Sans machine » pour une livraison sur un site seul. */
export function libelleMachine(asset: BonResume['asset']): string {
  if (asset === null) return 'Sans machine';
  if (asset.label) return asset.label;
  const morceaux = [asset.model, asset.serialNumber ? `n° ${asset.serialNumber}` : null].filter(Boolean);
  return morceaux.length > 0 ? morceaux.join(' ') : 'Machine sans désignation';
}

/** « BI-2026-00012 · Entretien / maintenance » */
export function titreBon(bon: Pick<BonResume, 'number' | 'nature'>): string {
  return `${bon.number} · ${bon.nature.label}`;
}

/** Le numéro Praxedo, quand le bon en reprend un. */
export function mentionPraxedo(bon: Pick<BonResume, 'praxedoNumber'>): string | null {
  return bon.praxedoNumber ? `n° Praxedo ${bon.praxedoNumber}` : null;
}

/** Les états où le contenu du bon se saisit : un brouillon, ou une intervention encore à faire au planning (ADR-0016). */
export const ETATS_SAISISSABLES: readonly EtatBon[] = ['DRAFT', 'TO_ASSIGN', 'PLANNED', 'IN_PROGRESS'];

export function etatSaisissable(etat: EtatBon): boolean {
  return ETATS_SAISISSABLES.includes(etat);
}

/** Une intervention du planning, pas encore faite : son bon se saisit sur elle (« Saisir le bon »). */
export function interventionAFaire(etat: EtatBon): boolean {
  return etat === 'TO_ASSIGN' || etat === 'PLANNED' || etat === 'IN_PROGRESS';
}

export type ActionBon = 'modifier' | 'soumettre' | 'valider' | 'rouvrir' | 'annuler';

export interface ActionPossible {
  readonly action: ActionBon;
  readonly libelle: string;
  /** Pourquoi le bouton est grisé ; absent = utilisable. */
  readonly raison?: string;
}

/**
 * Les gestes proposés sur un bon, selon son état et les droits. Saisir (modifier, soumettre, rouvrir, annuler) et
 * valider sont deux droits distincts : sans aucun des deux, l'écran se lit. Valider écrit dans la machine (relevés,
 * entretien) : il est proposé à qui détient le droit de valider, même s'il ne saisit pas.
 * Un bon dont la machine a été archivée ne se soumet ni ne se valide plus (on l'annule) : le serveur le refuserait.
 */
export function actionsDuBon(
  bon: Pick<BonResume, 'status' | 'asset'>,
  droits: Pick<DroitsBons, 'peutSaisirBons' | 'peutValiderBons'>,
): ActionPossible[] {
  const archivee = 'La machine de ce bon a été archivée : annulez le bon.';
  switch (bon.status) {
    // Une intervention du planning : son bon se saisit sur elle, puis se soumet. L'annuler, la déplacer, l'affecter : au planning.
    case 'TO_ASSIGN':
    case 'PLANNED':
    case 'IN_PROGRESS':
      if (!droits.peutSaisirBons) return [];
      return [
        { action: 'modifier', libelle: 'Saisir le bon' },
        bon.asset?.archived
          ? { action: 'soumettre', libelle: 'Soumettre à validation', raison: archivee }
          : { action: 'soumettre', libelle: 'Soumettre à validation' },
      ];
    case 'DRAFT':
      if (!droits.peutSaisirBons) return [];
      return [
        { action: 'modifier', libelle: 'Modifier' },
        bon.asset?.archived
          ? { action: 'soumettre', libelle: 'Soumettre à validation', raison: archivee }
          : { action: 'soumettre', libelle: 'Soumettre à validation' },
        { action: 'annuler', libelle: 'Annuler le bon' },
      ];
    case 'TO_REVIEW': {
      const gestes: ActionPossible[] = [];
      if (droits.peutValiderBons) {
        gestes.push(
          bon.asset?.archived
            ? { action: 'valider', libelle: 'Valider le bon', raison: archivee }
            : { action: 'valider', libelle: 'Valider le bon' },
        );
      }
      if (droits.peutSaisirBons) {
        gestes.push(
          { action: 'rouvrir', libelle: 'Rouvrir pour corriger' },
          { action: 'annuler', libelle: 'Annuler le bon' },
        );
      }
      return gestes;
    }
    default:
      return [];
  }
}

/** Ce que dit le détail d'un bon quand on ne peut rien en faire : pourquoi, en une phrase. */
export function phraseEtat(bon: Pick<BonResume, 'status' | 'number'> & { cancelReason?: string | null }): string {
  switch (bon.status) {
    case 'DRAFT':
      return 'Brouillon : il se modifie librement et n’écrit rien dans les compteurs ni dans l’entretien.';
    case 'TO_ASSIGN':
    case 'PLANNED':
    case 'IN_PROGRESS':
      return 'Intervention au planning, pas encore faite : saisissez son bon, puis soumettez-le. Rien n’est écrit dans les compteurs ni dans l’entretien avant la validation.';
    case 'TO_REVIEW':
      return 'À valider : relisez-le puis validez-le (la validation écrit les relevés et l’entretien dans la machine), ou rouvrez-le pour le corriger.';
    case 'REVIEWED':
      return 'Validé : le bon est figé. Une heure fausse se corrige en rectifiant le relevé ou l’entretien depuis la fiche de la machine.';
    case 'CANCELLED':
      return bon.cancelReason ? `Annulé : ${bon.cancelReason}` : 'Annulé.';
    default:
      return LIBELLE_ETAT[bon.status];
  }
}
